import { useField } from 'formik';
import { useRef, useMemo, type JSX } from 'react';

import { controlError } from 'utils/error';
import { DateRange, CommonFieldProps } from 'types';

import { DatePicker, DateInputProps } from '../date-picker';

export interface DatePickerFieldProps
  extends
    Omit<DateInputProps, 'name' | 'onChange'>,
    Partial<Pick<DateInputProps, 'onChange'>>,
    CommonFieldProps {}

export const DatePickerField = ({
  name,
  label,
  onChange,
  selectsRange,
  controlErrorLabel = true,
  ...props
}: DatePickerFieldProps): JSX.Element => {
  const [field, meta, helpers] = useField(name);
  // A pick in this open (its close may run before the pick's value is in).
  const picked = useRef(false);

  const error = controlError(meta, name, label, controlErrorLabel);

  const value = field.value;
  const inputProps = useMemo(() => {
    if (selectsRange) {
      const range = value as DateRange;
      return {
        endDate: range?.to,
        startDate: range?.from,
        onChange: async (dates: any): Promise<void> => {
          const [from, to] = dates;
          await helpers.setValue({ to, from }, true);
          onChange?.(dates);
        },
      };
    } else {
      return {
        selected: value,
        // Validated with the picked date, then touched without validating
        // again: the close's own validation ran on the value before the
        // pick and left a valid date red (owner pass 5.1).
        onChange: async (date: any): Promise<void> => {
          picked.current = date !== null && date !== undefined;
          await helpers.setValue(date, true);
          await helpers.setTouched(true, false);
          onChange?.(date);
        },
      };
    }
  }, [value, selectsRange, onChange]);

  return (
    <DatePicker
      label={label}
      selectsRange={selectsRange}
      input={{
        error,
      }}
      // Opened and closed without a date: validate, so a required date
      // says so as before. After a pick: touch only (the pick validated with
      // its own value; a second validation here could read the value before
      // the pick and leave a valid date red, owner pass 5.1).
      onClose={() => {
        const empty =
          !picked.current &&
          (field.value === null ||
            field.value === undefined ||
            field.value === '');
        picked.current = false;
        helpers.setTouched(true, empty);
      }}
      {...field}
      {...props}
      {...inputProps}
    />
  );
};
